"use client";

import Image from "next/image";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useId, useRef, useState, useTransition } from "react";
import { A11y, Autoplay, Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperInstance } from "swiper";
import type { ProductCategory } from "@/types/category";
import { getCategoryHref } from "@/lib/routing/storefrontUrls";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

interface SubcategoryCarouselProps {
  category: ProductCategory;
  allCategories: ProductCategory[];
  subcategories: ProductCategory[];
  selectedSlug?: string;
  includeMainCategory?: boolean;
  autoplay?: boolean;
  onSelect?: (category: ProductCategory) => void;
}

const CATEGORY_FALLBACK = "/images/placeholders/category-placeholder.svg";

export function SubcategoryCarousel({
  category,
  allCategories,
  subcategories,
  selectedSlug,
  includeMainCategory = true,
  autoplay = true,
  onSelect,
}: SubcategoryCarouselProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const swiperRef = useRef<SwiperInstance | null>(null);
  const carouselId = useId().replaceAll(":", "");
  const previousClass = `subcategory-previous-${carouselId}`;
  const nextClass = `subcategory-next-${carouselId}`;
  const paginationClass = `subcategory-pagination-${carouselId}`;
  const [isPending, startTransition] = useTransition();
  const [hasOverflow, setHasOverflow] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const items = includeMainCategory
    ? [category, ...subcategories]
    : subcategories;

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);

    updatePreference();
    mediaQuery.addEventListener("change", updatePreference);

    return () => mediaQuery.removeEventListener("change", updatePreference);
  }, []);

  useEffect(() => {
    const swiper = swiperRef.current;

    if (!swiper?.autoplay) return;

    if (autoplay && hasOverflow && !prefersReducedMotion) {
      swiper.autoplay.start();
    } else {
      swiper.autoplay.stop();
    }
  }, [autoplay, hasOverflow, prefersReducedMotion]);

  function updateOverflow(swiper: SwiperInstance) {
    setHasOverflow(!swiper.isLocked);
  }

  function selectSubcategory(item: ProductCategory) {
    const params = new URLSearchParams(searchParams.toString());
    params.delete("subcategoria");
    params.delete("pagina");

    onSelect?.(item);

    const query = params.toString();
    startTransition(() => {
      const itemPathname = getCategoryHref(item, allCategories);
      router.push(query ? `${itemPathname}?${query}` : itemPathname, {
        scroll: false,
      });
    });
  }

  function pauseAutoplay() {
    swiperRef.current?.autoplay?.stop();
  }

  function resumeAutoplay() {
    if (autoplay && hasOverflow && !prefersReducedMotion) {
      swiperRef.current?.autoplay?.start();
    }
  }

  if (subcategories.length === 0) return null;

  return (
    <section
      className="group/subcategories relative mt-2 sm:mt-6 lg:mt-8"
      aria-label="Selecionar subcategoria"
      aria-busy={isPending}
      onFocusCapture={pauseAutoplay}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          resumeAutoplay();
        }
      }}
    >
      <Swiper
        modules={[Navigation, Pagination, Autoplay, A11y]}
        slidesPerView={3}
        slidesPerGroup={3}
        spaceBetween={8}
        speed={600}
        rewind
        watchOverflow
        observer
        observeParents
        navigation={{
          prevEl: `.${previousClass}`,
          nextEl: `.${nextClass}`,
        }}
        pagination={{
          el: `.${paginationClass}`,
          clickable: true,
        }}
        autoplay={{
          delay: 3800,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }}
        breakpoints={{
          480: {
            slidesPerView: 4,
            slidesPerGroup: 4,
            spaceBetween: 10,
          },
          640: {
            slidesPerView: 5,
            slidesPerGroup: 5,
            spaceBetween: 12,
          },
          768: {
            slidesPerView: 6,
            slidesPerGroup: 6,
            spaceBetween: 14,
          },
          1024: {
            slidesPerView: 8,
            slidesPerGroup: 8,
            spaceBetween: 16,
          },
          1280: {
            slidesPerView: 10,
            slidesPerGroup: 10,
            spaceBetween: 16,
          },
        }}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
          updateOverflow(swiper);
        }}
        onAfterInit={updateOverflow}
        onBreakpoint={updateOverflow}
        onResize={updateOverflow}
        onLock={() => setHasOverflow(false)}
        onUnlock={() => setHasOverflow(true)}
        className="pb-5! sm:pb-7!"
      >
        {items.map((item) => {
          const isSelected =
            includeMainCategory && item.id === category.id
              ? !selectedSlug
              : item.slug === selectedSlug;

          return (
            <SwiperSlide key={item.id} className="h-auto!">
              <button
                type="button"
                onClick={() => selectSubcategory(item)}
                disabled={isPending}
                aria-pressed={isSelected}
                aria-current={isSelected ? "page" : undefined}
                className="tap-feedback group/item flex h-full w-full flex-col items-center rounded-xl py-1 text-center disabled:cursor-wait disabled:opacity-60"
              >
                <span
                  className={`flex aspect-square w-14 items-center justify-center overflow-hidden rounded-full bg-white p-1 transition-[box-shadow] sm:w-16 lg:w-20 ${
                    isSelected
                      ? "ring-2 ring-secondary ring-offset-2"
                      : "ring-1 ring-slate-200"
                  }`}
                >
                  <Image
                    src={item.image?.src ?? CATEGORY_FALLBACK}
                    alt={item.image?.alt || item.name}
                    width={80}
                    height={80}
                    sizes="(min-width: 1024px) 80px, (min-width: 640px) 64px, 56px"
                    className="h-full w-full rounded-full object-contain transition-transform group-hover/item:scale-105"
                  />
                </span>
                <span
                  className={`mt-2 line-clamp-3 text-xs font-medium leading-4 sm:text-sm ${
                    isSelected ? "text-secondary" : "text-foreground"
                  }`}
                >
                  {item.name}
                </span>
              </button>
            </SwiperSlide>
          );
        })}
      </Swiper>

      <button
        type="button"
        className={`${previousClass} absolute top-10 left-0 z-10 h-10 w-10 -translate-x-1/3 items-center justify-center rounded-full bg-white/90 text-foreground opacity-0 shadow-sm transition-opacity hover:text-secondary focus-visible:opacity-100 ${
          hasOverflow
            ? "hidden lg:flex lg:group-hover/subcategories:opacity-100"
            : "hidden"
        }`}
        aria-label="Ver subcategorias anteriores"
        aria-hidden={!hasOverflow}
        disabled={!hasOverflow}
      >
        <ChevronLeft className="h-5 w-5" aria-hidden="true" />
      </button>
      <button
        type="button"
        className={`${nextClass} absolute top-10 right-0 z-10 h-10 w-10 translate-x-1/3 items-center justify-center rounded-full bg-white/90 text-foreground opacity-0 shadow-sm transition-opacity hover:text-secondary focus-visible:opacity-100 ${
          hasOverflow
            ? "hidden lg:flex lg:group-hover/subcategories:opacity-100"
            : "hidden"
        }`}
        aria-label="Ver próximas subcategorias"
        aria-hidden={!hasOverflow}
        disabled={!hasOverflow}
      >
        <ChevronRight className="h-5 w-5" aria-hidden="true" />
      </button>
      <div
        className={`${paginationClass} pagination-pill absolute inset-x-0 bottom-0 z-10 h-4 items-center justify-center gap-2 ${
          hasOverflow ? "flex" : "hidden"
        }`}
        aria-label="Paginação das subcategorias"
        aria-hidden={!hasOverflow}
      />
    </section>
  );
}
